import axios from 'axios';

// Single shared Axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  timeout: 15000,
  // NOTE: Do NOT set Content-Type here — Axios sets it automatically
  // for JSON (application/json) and FormData (multipart/form-data with boundary)
});

// Attach JWT on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, Promise.reject);

// Handle 401 globally — redirect to login
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname.startsWith('/admin') &&
          !window.location.pathname.startsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:          (d) => api.post('/auth/login', d),
  logout:         ()  => api.post('/auth/logout'),
  me:             ()  => api.get('/auth/me'),
  updateProfile:  (d) => api.put('/auth/profile', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  changePassword: (d) => api.put('/auth/password', d),
};

// ─── Public APIs ──────────────────────────────────────────────────────────────
export const sliderAPI = {
  getPublic: ()  => api.get('/sliders'),
  getAll:    ()  => api.get('/admin/sliders'),
  create:    (d) => api.post('/admin/sliders', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:    (id,d) => api.put(`/admin/sliders/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:    (id)   => api.delete(`/admin/sliders/${id}`),
};

export const productAPI = {
  getAll:       (p) => api.get('/products', { params: p }),
  getBySlug:    (s) => api.get(`/products/${s}`),
  getCategories:()  => api.get('/products/categories'),
  adminGetAll:  (p) => api.get('/admin/products', { params: p }),
  create:       (d) => api.post('/admin/products', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:       (id,d) => api.put(`/admin/products/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:       (id)   => api.delete(`/admin/products/${id}`),
};

export const packageAPI = {
  getAll:       (p) => api.get('/packages', { params: p }),
  getBySlug:    (s) => api.get(`/packages/${s}`),
  getCategories:()  => api.get('/packages/categories'),
  adminGetAll:  (p) => api.get('/admin/packages', { params: p }),
  create:       (d) => api.post('/admin/packages', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:       (id,d) => api.put(`/admin/packages/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:       (id)   => api.delete(`/admin/packages/${id}`),
};

export const newsAPI = {
  getAll:     (p) => api.get('/news', { params: p }),
  getBySlug:  (s) => api.get(`/news/${s}`),
  adminGetAll:(p) => api.get('/admin/news', { params: p }),
  create:     (d) => api.post('/admin/news', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:     (id,d) => api.put(`/admin/news/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:     (id)   => api.delete(`/admin/news/${id}`),
};

export const offerAPI = {
  getAll:     () => api.get('/offers'),
  adminGetAll:() => api.get('/admin/offers'),
  create:     (d) => api.post('/admin/offers', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:     (id,d) => api.put(`/admin/offers/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:     (id)   => api.delete(`/admin/offers/${id}`),
};

export const testimonialAPI = {
  getPublic:     (p) => api.get('/testimonials', { params: p }),
  getAdmin:      ()  => api.get('/admin/testimonials'),
  create:        (d) => api.post('/admin/testimonials', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:        (id,d) => api.put(`/admin/testimonials/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  toggleFeature: (id)   => api.put(`/admin/testimonials/${id}/feature`),
  toggleApprove: (id)   => api.put(`/admin/testimonials/${id}/approve`),
  delete:        (id)   => api.delete(`/admin/testimonials/${id}`),
};

export const inquiryAPI = {
  submit:       (d)   => api.post('/inquiries', d),
  getAll:       (p)   => api.get('/admin/inquiries', { params: p }),
  getStats:     ()    => api.get('/admin/inquiries/stats'),
  getById:      (id)  => api.get(`/admin/inquiries/${id}`),
  updateStatus: (id,d)=> api.put(`/admin/inquiries/${id}/status`, d),
  reply:        (id,d)=> api.post(`/admin/inquiries/${id}/reply`, d),
};

export const adminAPI = {
  getDashboard:   () => api.get('/admin/dashboard'),
  getUsers:       (p) => api.get('/admin/users', { params: p }),
  createUser:     (d) => api.post('/admin/users', d),
  updateUser:     (id,d) => api.put(`/admin/users/${id}`, d),
  resetPassword:  (id,d) => api.put(`/admin/users/${id}/password`, d),
  deleteUser:     (id)   => api.delete(`/admin/users/${id}`),
  getSubscribers:       ()   => api.get('/admin/subscribers'),
  toggleSubscriber:     (id) => api.patch(`/admin/subscribers/${id}/toggle`),
  deleteSubscriber:     (id) => api.delete(`/admin/subscribers/${id}`),
  patch:                (url, data) => api.patch(url, data),
  delete:               (url) => api.delete(url),
  getSettings:    () => api.get('/settings'),
  updateSettings: (d) => api.put('/admin/settings', d),
  uploadLogo:     (d) => api.post('/admin/settings/logo', d, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const subscribeAPI = {
  subscribe:   (d)    => api.post('/subscribe', d),
  unsubscribe: (token)=> api.get(`/unsubscribe?token=${token}`),
};

export default api;

// ─── Resolve backend upload URLs ──────────────────────────────────────────────
// In dev, React runs on :3000 and backend on :5000.
// Uploaded images are at /uploads/... on the backend, not served by React.
// This helper converts relative upload paths to absolute backend URLs.
export const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;  // already absolute
  if (url.startsWith('/uploads/')) {
    const base = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace('/api', '')
      : '';
    return `${base}${url}`;
  }
  return url; // local /images/... files — React serves these fine
};

// ─── Career API ───────────────────────────────────────────────────────────────
export const careerAPI = {
  getJobs:           ()       => api.get('/careers'),
  getJob:            (id)     => api.get(`/careers/${id}`),
  apply:             (d)      => api.post('/careers/apply', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  adminGetJobs:      ()       => api.get('/admin/careers'),
  adminCreate:       (d)      => api.post('/admin/careers', d),
  adminUpdate:       (id, d)  => api.put(`/admin/careers/${id}`, d),
  adminDelete:       (id)     => api.delete(`/admin/careers/${id}`),
  adminGetApps:      (params) => api.get('/admin/careers/applications', { params }),
  adminUpdateApp:    (id, d)  => api.put(`/admin/careers/applications/${id}`, d),
};

// ─── Team API ─────────────────────────────────────────────────────────────────
export const teamAPI = {
  getAll:      ()       => api.get('/team'),
  adminGetAll: ()       => api.get('/admin/team'),
  create:      (d)      => api.post('/admin/team', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:      (id, d)  => api.put(`/admin/team/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:      (id)     => api.delete(`/admin/team/${id}`),
};
